import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HeartIcon, HandshakeIcon, DollarSignIcon, LightbulbIcon, UsersIcon, ClipboardCheckIcon, GlobeIcon, BookOpenIcon, AwardIcon, BriefcaseIcon, CheckCircleIcon, ArrowRightIcon, CreditCardIcon, BuildingIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const SupportUsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [donationAmount, setDonationAmount] = useState('100');
  const [donationFrequency, setDonationFrequency] = useState('monthly');
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const donationTiers = [{
    name: 'Supporter',
    amount: 'Monthly: $10-49',
    oneTime: 'One-time: $100-499',
    benefits: ['Recognition on our donor wall', 'Quarterly newsletter with research updates', 'Early access to educational content'],
    icon: <HeartIcon size={24} className="text-blue-600" data-id="element-1889" />
  }, {
    name: 'Advocate',
    amount: 'Monthly: $50-99',
    oneTime: 'One-time: $500-999',
    benefits: ['All Supporter benefits', 'Annual impact report', 'Invitation to virtual research presentations', 'Access to exclusive webinars'],
    icon: <BookOpenIcon size={24} className="text-blue-600" data-id="element-1890" />
  }, {
    name: 'Champion',
    amount: 'Monthly: $100-249',
    oneTime: 'One-time: $1,000-2,499',
    benefits: ['All Advocate benefits', 'Name recognition in annual publication', 'Quarterly virtual meet-and-greets with researchers', 'Personalized updates on research projects'],
    icon: <AwardIcon size={24} className="text-blue-600" data-id="element-1891" />
  }, {
    name: 'Visionary',
    amount: 'Monthly: $250+',
    oneTime: 'One-time: $2,500+',
    benefits: ['All Champion benefits', 'Lab tour (virtual or in-person)', 'Recognition on major publications', 'Annual 1:1 consultation with research team', 'Input on future research priorities'],
    icon: <LightbulbIcon size={24} className="text-blue-600" data-id="element-1892" />
  }];
  const partnershipOpportunities = [{
    title: 'Research Collaboration',
    description: 'Partner with our scientists on joint research initiatives investigating microbiome science and therapeutic applications.',
    icon: <ClipboardCheckIcon size={24} className="text-blue-600" data-id="element-1893" />
  }, {
    title: 'Corporate Sponsorship',
    description: 'Support specific research programs or educational initiatives with recognition and branding opportunities.',
    icon: <BuildingIcon size={24} className="text-blue-600" data-id="element-1894" />
  }, {
    title: 'Educational Partnerships',
    description: 'Co-develop educational content, webinars, or workshops to advance understanding of microbiome science.',
    icon: <BookOpenIcon size={24} className="text-blue-600" data-id="element-1895" />
  }, {
    title: 'Technology Partners',
    description: 'Provide equipment, software, or technical expertise to enhance our research capabilities.',
    icon: <GlobeIcon size={24} className="text-blue-600" data-id="element-1896" />
  }];
  const impactMetrics = [{
    metric: '25+',
    description: 'Research studies published',
    icon: <ClipboardCheckIcon size={24} className="text-blue-600" data-id="element-1897" />
  }, {
    metric: '10,000+',
    description: 'People educated through our resources',
    icon: <UsersIcon size={24} className="text-blue-600" data-id="element-1898" />
  }, {
    metric: '100+',
    description: 'Products tested for efficacy and safety',
    icon: <CheckCircleIcon size={24} className="text-blue-600" data-id="element-1899" />
  }, {
    metric: '12',
    description: 'Countries where our research is implemented',
    icon: <GlobeIcon size={24} className="text-blue-600" data-id="element-1900" />
  }];
  const testimonials = [{
    quote: "Supporting the Ukweli Institute has been incredibly rewarding. Their commitment to scientific rigor in the wellness space is unmatched, and I'm proud to contribute to research that helps millions make better health decisions.",
    name: 'Dr. Michael Roberts',
    title: 'Monthly Donor since 2022',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
  }, {
    quote: "As a corporate partner, we've seen firsthand how the Institute's work elevates the entire supplement industry. Their research has helped us develop better products and build consumer trust through evidence-based claims.",
    name: 'Sarah Johnson',
    title: 'CEO, NutriWell Labs',
    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
  }, {
    quote: 'I volunteer my expertise because I believe in the mission. The Ukweli Institute is creating a much-needed bridge between consumer wellness products and rigorous scientific validation.',
    name: 'James Chen, PhD',
    title: 'Scientific Advisory Board Volunteer',
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80'
  }];
  const handleDonationAmountChange = e => {
    setDonationAmount(e.target.value);
  };
  return <div className="bg-white w-full" data-id="element-1901">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-1902">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1903">
          <div className="text-center" data-id="element-1904">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-1905">
              Support Our Mission
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-1906">
              Help us advance evidence-based science in the wellness industry
              and empower consumers with trustworthy information
            </p>
          </div>
        </div>
      </section>
      {/* Why Support Section */}
      <section className="py-16 bg-white" data-id="element-1907">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1908">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="text-center mb-12" data-id="element-1909">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1910">
              Why Support the Ukweli Institute?
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto" data-id="element-1911">
              The Ukweli Institute is creating a new standard for evidence-based
              wellness through independent research, education, and
              certification. Your support enables us to continue this critical
              work without commercial bias or influence.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mt-12" data-id="element-1912">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1913">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1914">
                Your Support Enables Us To:
              </h3>
              <ul className="space-y-4" data-id="element-1915">
                <li className="flex items-start" data-id="element-1916">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3 mt-1" data-id="element-1917">
                    <CheckCircleIcon size={16} data-id="element-1918" />
                  </div>
                  <p className="text-gray-700" data-id="element-1919">
                    <strong data-id="element-1920">Conduct Independent Research</strong> — Test
                    supplements, probiotics, and wellness products with
                    pharmaceutical-grade rigor, free from industry influence
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1921">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3 mt-1" data-id="element-1922">
                    <CheckCircleIcon size={16} data-id="element-1923" />
                  </div>
                  <p className="text-gray-700" data-id="element-1924">
                    <strong data-id="element-1925">Provide Unbiased Education</strong> — Create
                    evidence-based resources that help consumers and
                    practitioners navigate complex health decisions
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1926">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3 mt-1" data-id="element-1927">
                    <CheckCircleIcon size={16} data-id="element-1928" />
                  </div>
                  <p className="text-gray-700" data-id="element-1929">
                    <strong data-id="element-1930">Develop Certification Standards</strong> — Establish
                    rigorous benchmarks that elevate the entire wellness
                    industry and protect consumers
                  </p>
                </li>
                <li className="flex items-start" data-id="element-1931">
                  <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 mr-3 mt-1" data-id="element-1932">
                    <CheckCircleIcon size={16} data-id="element-1933" />
                  </div>
                  <p className="text-gray-700" data-id="element-1934">
                    <strong data-id="element-1935">Build Global Research Networks</strong> —
                    Collaborate with scientists worldwide to advance microbiome
                    science and therapeutic applications
                  </p>
                </li>
              </ul>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1936">
              <img src="https://images.unsplash.com/photo-1581093458791-9a9cd7093ae3?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" alt="Laboratory research at Ukweli Institute" className="rounded-lg shadow-xl w-full h-auto" onError={e => {
              e.currentTarget.src = 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80';
            }} data-id="element-1937" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Ways to Support Section */}
      <section className="py-16 bg-gray-50" data-id="element-1938">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1939">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-1940">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1941">
              Ways to Support Our Mission
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-1942">
              There are many ways to contribute to the Ukweli Institute's work.
              Choose the option that best aligns with your interests and
              capacity.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1943">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white rounded-xl overflow-hidden shadow-md" data-id="element-1944">
              <div className="p-8" data-id="element-1945">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-1946">
                  <DollarSignIcon size={28} data-id="element-1947" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1948">
                  Financial Support
                </h3>
                <p className="text-gray-700 mb-6" data-id="element-1949">
                  Make a one-time or recurring donation to fund our research and
                  educational initiatives. Every contribution, regardless of
                  size, helps advance our mission.
                </p>
                <a href="#donate" className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800" data-id="element-1950">
                  Donate now <ArrowRightIcon size={16} className="ml-1" data-id="element-1951" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white rounded-xl overflow-hidden shadow-md" data-id="element-1952">
              <div className="p-8" data-id="element-1953">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-1954">
                  <HandshakeIcon size={28} data-id="element-1955" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1956">
                  Partnerships
                </h3>
                <p className="text-gray-700 mb-6" data-id="element-1957">
                  Collaborate with us through research partnerships, corporate
                  sponsorships, or educational initiatives that align with your
                  organization's values.
                </p>
                <a href="#partnerships" className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800" data-id="element-1958">
                  Explore partnerships{' '}
                  <ArrowRightIcon size={16} className="ml-1" data-id="element-1959" />
                </a>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white rounded-xl overflow-hidden shadow-md" data-id="element-1960">
              <div className="p-8" data-id="element-1961">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6" data-id="element-1962">
                  <HeartIcon size={28} data-id="element-1963" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1964">
                  Volunteer
                </h3>
                <p className="text-gray-700 mb-6" data-id="element-1965">
                  Contribute your expertise, skills, or time to our research,
                  education, or outreach initiatives. We welcome professionals
                  from diverse backgrounds.
                </p>
                <a href="#volunteer" className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800" data-id="element-1966">
                  Get involved <ArrowRightIcon size={16} className="ml-1" data-id="element-1967" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Donation Section */}
      <section id="donate" className="py-16 bg-white" data-id="element-1968">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1969">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-1970">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1971">
              Make a Donation
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-1972">
              Your financial support directly enables our independent research
              and educational initiatives. Choose a one-time or recurring
              donation.
            </p>
            <div className="mt-4" data-id="element-1973">
              <Link to="/donor-recognition" className="inline-flex items-center font-medium text-blue-600 hover:text-blue-800" data-id="element-1974">
                View our Donor Recognition Wall{' '}
                <ArrowRightIcon size={16} className="ml-1" data-id="element-1975" />
              </Link>
            </div>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-1976">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-gray-50 rounded-xl p-8" data-id="element-1977">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1978">
                Donation Form
              </h3>
              <div className="mb-6" data-id="element-1979">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-1980">
                  Select Donation Frequency
                </label>
                <div className="flex space-x-4" data-id="element-1981">
                  <button className={`flex-1 py-3 px-4 rounded-md font-medium ${donationFrequency === 'monthly' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setDonationFrequency('monthly')} data-id="element-1982">
                    Monthly
                  </button>
                  <button className={`flex-1 py-3 px-4 rounded-md font-medium ${donationFrequency === 'one-time' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setDonationFrequency('one-time')} data-id="element-1983">
                    One-time
                  </button>
                </div>
              </div>
              <div className="mb-6" data-id="element-1984">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-1985">
                  Select Amount
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-id="element-1986">
                  {['25', '50', '100', '250'].map(amount => <button key={amount} className={`py-3 px-4 rounded-md font-medium ${donationAmount === amount ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'}`} onClick={() => setDonationAmount(amount)} data-id="element-1987">
                      ${amount}
                    </button>)}
                </div>
                <div className="relative" data-id="element-1988">
                  <span className="absolute left-3 top-3 text-gray-500" data-id="element-1989">$</span>
                  <input type="number" className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Other amount" value={donationAmount} onChange={handleDonationAmountChange} data-id="element-1990" />
                </div>
              </div>
              <div className="mb-6" data-id="element-1991">
                <label className="block text-gray-700 font-medium mb-2" data-id="element-1992">
                  Personal Information
                </label>
                <div className="space-y-3" data-id="element-1993">
                  <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Full Name" data-id="element-1994" />
                  <input type="email" className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Email Address" data-id="element-1995" />
                </div>
              </div>
              <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200 flex items-center justify-center" data-id="element-1996">
                <CreditCardIcon size={18} className="mr-2" data-id="element-1997" />
                Proceed to Payment
              </button>
              <p className="text-sm text-gray-500 mt-4 text-center" data-id="element-1998">
                Your donation is tax-deductible to the extent allowed by law.
                You will receive a receipt for your records.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-1999">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2000">
                Donor Recognition Tiers
              </h3>
              <div className="space-y-6" data-id="element-2001">
                {donationTiers.map((tier, index) => <div key={tier.name} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow duration-300" data-id="element-2002">
                    <div className="flex items-start" data-id="element-2003">
                      <div className="flex-shrink-0 mr-4" data-id="element-2004">
                        <div className="bg-blue-50 rounded-full p-3" data-id="element-2005">
                          {tier.icon}
                        </div>
                      </div>
                      <div data-id="element-2006">
                        <h4 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2007">
                          {tier.name}
                        </h4>
                        <div className="flex flex-wrap gap-2 mb-3" data-id="element-2008">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2009">
                            {tier.amount}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2010">
                            {tier.oneTime}
                          </span>
                        </div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-2011">
                          Benefits include:
                        </h5>
                        <ul className="space-y-1" data-id="element-2012">
                          {tier.benefits.map((benefit, i) => <li key={i} className="flex items-start text-sm text-gray-600" data-id="element-2013">
                              <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-2014" />
                              <span data-id="element-2015">{benefit}</span>
                            </li>)}
                        </ul>
                      </div>
                    </div>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Partnership Opportunities Section */}
      <section id="partnerships" className="py-16 bg-gray-50" data-id="element-2016">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2017">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2018">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2019">
              Partnership Opportunities
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-2020">
              Partner with the Ukweli Institute to advance microbiome science
              while aligning with your organization's goals and values.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2021">
            {partnershipOpportunities.map((opportunity, index) => <motion.div key={opportunity.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white p-6 rounded-lg shadow-sm" data-id="element-2022">
                <div className="flex items-start" data-id="element-2023">
                  <div className="flex-shrink-0 mr-4" data-id="element-2024">
                    <div className="bg-blue-50 rounded-full p-3" data-id="element-2025">
                      {opportunity.icon}
                    </div>
                  </div>
                  <div data-id="element-2026">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2027">
                      {opportunity.title}
                    </h3>
                    <p className="text-gray-700" data-id="element-2028">{opportunity.description}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="mt-12 text-center" data-id="element-2029">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300" data-id="element-2030">
              <HandshakeIcon size={18} className="mr-2" data-id="element-2031" />
              Discuss Partnership Opportunities
            </Link>
            <p className="mt-4 text-gray-600" data-id="element-2032">
              Contact us to discuss custom partnership arrangements that align
              with your organization's goals
            </p>
          </motion.div>
        </div>
      </section>
      {/* Volunteer Section */}
      <section id="volunteer" className="py-16 bg-white" data-id="element-2033">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2034">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2035">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2036">
              Volunteer Opportunities
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-2037">
              Contribute your expertise and skills to help advance our mission.
              We welcome volunteers from diverse backgrounds.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2038">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-gray-50 rounded-lg p-6" data-id="element-2039">
              <div className="bg-blue-100 rounded-full p-3 inline-block mb-4" data-id="element-2040">
                <BriefcaseIcon size={24} className="text-blue-600" data-id="element-2041" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2042">
                Professional Expertise
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-2043">
                Volunteer your professional skills in areas such as:
              </p>
              <ul className="space-y-2 mb-6" data-id="element-2044">
                <li className="flex items-start" data-id="element-2045">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2046" />
                  <span className="text-gray-700" data-id="element-2047">
                    Scientific research and analysis
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2048">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2049" />
                  <span className="text-gray-700" data-id="element-2050">
                    Medical and healthcare expertise
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2051">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2052" />
                  <span className="text-gray-700" data-id="element-2053">
                    Legal and regulatory knowledge
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2054">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2055" />
                  <span className="text-gray-700" data-id="element-2056">
                    Marketing and communications
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2057">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2058" />
                  <span className="text-gray-700" data-id="element-2059">IT and data science</span>
                </li>
              </ul>
              <Link to="/contact" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-2060">
                Apply as a professional volunteer{' '}
                <ArrowRightIcon size={16} className="ml-1" data-id="element-2061" />
              </Link>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-gray-50 rounded-lg p-6" data-id="element-2062">
              <div className="bg-blue-100 rounded-full p-3 inline-block mb-4" data-id="element-2063">
                <BookOpenIcon size={24} className="text-blue-600" data-id="element-2064" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2065">
                Educational Initiatives
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-2066">
                Help us develop and deliver educational content:
              </p>
              <ul className="space-y-2 mb-6" data-id="element-2067">
                <li className="flex items-start" data-id="element-2068">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2069" />
                  <span className="text-gray-700" data-id="element-2070">
                    Content creation and editing
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2071">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2072" />
                  <span className="text-gray-700" data-id="element-2073">
                    Webinar and workshop facilitation
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2074">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2075" />
                  <span className="text-gray-700" data-id="element-2076">Curriculum development</span>
                </li>
                <li className="flex items-start" data-id="element-2077">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2078" />
                  <span className="text-gray-700" data-id="element-2079">Translation services</span>
                </li>
                <li className="flex items-start" data-id="element-2080">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2081" />
                  <span className="text-gray-700" data-id="element-2082">Educational outreach</span>
                </li>
              </ul>
              <Link to="/contact" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-2083">
                Join our education team{' '}
                <ArrowRightIcon size={16} className="ml-1" data-id="element-2084" />
              </Link>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-gray-50 rounded-lg p-6" data-id="element-2085">
              <div className="bg-blue-100 rounded-full p-3 inline-block mb-4" data-id="element-2086">
                <UsersIcon size={24} className="text-blue-600" data-id="element-2087" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2088">
                Community Engagement
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-2089">
                Help us connect with communities and spread our mission:
              </p>
              <ul className="space-y-2 mb-6" data-id="element-2090">
                <li className="flex items-start" data-id="element-2091">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2092" />
                  <span className="text-gray-700" data-id="element-2093">
                    Event organization and support
                  </span>
                </li>
                <li className="flex items-start" data-id="element-2094">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2095" />
                  <span className="text-gray-700" data-id="element-2096">Community outreach</span>
                </li>
                <li className="flex items-start" data-id="element-2097">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2098" />
                  <span className="text-gray-700" data-id="element-2099">Social media advocacy</span>
                </li>
                <li className="flex items-start" data-id="element-2100">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2101" />
                  <span className="text-gray-700" data-id="element-2102">Fundraising support</span>
                </li>
                <li className="flex items-start" data-id="element-2103">
                  <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-2104" />
                  <span className="text-gray-700" data-id="element-2105">Ambassador program</span>
                </li>
              </ul>
              <Link to="/contact" className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-2106">
                Become a community volunteer{' '}
                <ArrowRightIcon size={16} className="ml-1" data-id="element-2107" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Impact Section */}
      <section className="py-16 bg-gray-50" data-id="element-2108">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2109">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2110">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2111">
              Our Impact
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-2112">
              Your support enables real-world impact through research,
              education, and certification.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2113">
            {impactMetrics.map((item, index) => <motion.div key={item.description} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-white rounded-lg p-8 text-center shadow-sm" data-id="element-2114">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4" data-id="element-2115">
                  {item.icon}
                </div>
                <div className="text-4xl font-bold text-blue-600 mb-2" data-id="element-2116">
                  {item.metric}
                </div>
                <p className="text-gray-700" data-id="element-2117">{item.description}</p>
              </motion.div>)}
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="mt-12 bg-white p-8 rounded-lg shadow-md" data-id="element-2118">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-2119">
              How Your Support Makes a Difference
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2120">
              <div className="text-center" data-id="element-2121">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 mb-4" data-id="element-2122">
                  <DollarSignIcon size={24} data-id="element-2123" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2124">
                  $10,000
                </h4>
                <p className="text-gray-700" data-id="element-2125">
                  Funds a comprehensive product testing program for safety and
                  efficacy
                </p>
              </div>
              <div className="text-center" data-id="element-2126">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 mb-4" data-id="element-2127">
                  <DollarSignIcon size={24} data-id="element-2128" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2129">
                  $25,000
                </h4>
                <p className="text-gray-700" data-id="element-2130">
                  Enables a pilot clinical trial to validate therapeutic
                  approaches
                </p>
              </div>
              <div className="text-center" data-id="element-2131">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 mb-4" data-id="element-2132">
                  <DollarSignIcon size={24} data-id="element-2133" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-2134">
                  $50,000
                </h4>
                <p className="text-gray-700" data-id="element-2135">
                  Supports a year-long research program with multiple studies
                  and publications
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="py-16 bg-white" data-id="element-2136">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2137">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-12" data-id="element-2138">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-2139">
              From Our Supporters
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-2140">
              Hear from the individuals and organizations who support our
              mission.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2141">
            {testimonials.map((testimonial, index) => <motion.div key={testimonial.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-gray-50 rounded-lg p-6 shadow-sm" data-id="element-2142">
                <div className="mb-6" data-id="element-2143">
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2144" />
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2145" />
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2146" />
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2147" />
                  <StarIcon size={20} className="text-yellow-500 inline-block" data-id="element-2148" />
                </div>
                <p className="text-gray-700 mb-6 italic" data-id="element-2149">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center" data-id="element-2150">
                  <img src={testimonial.image} alt={testimonial.name} className="h-12 w-12 rounded-full object-cover mr-4" onError={e => {
                e.currentTarget.src = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=250&q=80';
              }} data-id="element-2151" />
                  <div data-id="element-2152">
                    <h4 className="font-bold text-gray-900" data-id="element-2153">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-gray-600" data-id="element-2154">{testimonial.title}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-blue-50" data-id="element-2155">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2156">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-600 rounded-xl p-8 md:p-12 text-center" data-id="element-2157">
            <h2 className="text-3xl font-bold text-white mb-6" data-id="element-2158">
              Join Us in Our Mission
            </h2>
            <p className="text-lg text-blue-100 max-w-3xl mx-auto mb-8" data-id="element-2159">
              Together, we can transform the wellness industry with
              evidence-based science and empower consumers with trustworthy
              information.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2160">
              <a href="#donate" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-blue-50 transition-colors duration-300 flex items-center justify-center" data-id="element-2161">
                <DollarSignIcon size={18} className="mr-2" data-id="element-2162" />
                Make a Donation
              </a>
              <Link to="/contact" className="px-6 py-3 bg-blue-700 text-white font-medium rounded-md shadow-md hover:bg-blue-800 transition-colors duration-300 flex items-center justify-center" data-id="element-2163">
                <HandshakeIcon size={18} className="mr-2" data-id="element-2164" />
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};